<script setup lang="ts">
import { ref, watch } from 'vue'

import BaseModal from '@/components/BaseModal.vue'
import { useIOSKeyboard } from '@/composables/useIOSKeyboard'
import {
  debounce,
  searchNominatim,
  getLocationName,
  formatAddress,
  type NominatimResult,
} from '@/services/nominatimService'
import { mapSearchResultToPOIType } from '@/services/poiService'
import { isSmallScreen } from '@/services/uiService'

const props = defineProps<{
  isOpen: boolean
  placementScope?: 'tour-waypoint' | 'workspace-poi'
}>()

const emit = defineEmits<{
  close: []
  'close-tour-modal-and-collapse-mobile-toolbar': []
}>()

const { getInputHandlers } = useIOSKeyboard()

const query = ref('')
const results = ref<NominatimResult[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const hasSearched = ref(false)
const isPOIScope = () => props.placementScope === 'workspace-poi'

let abortController: AbortController | null = null

const doSearch = async () => {
  const q = query.value.trim()
  if (!q) {
    results.value = []
    error.value = null
    return
  }
  try {
    loading.value = true
    error.value = null
    hasSearched.value = true
    if (abortController) {
      abortController.abort()
    }
    abortController = new AbortController()
    results.value = await searchNominatim(q, {
      signal: abortController.signal,
      limit: 8,
    })
  } catch (e: unknown) {
    if ((e as Error).name === 'AbortError') return
    error.value = (e as Error).message || 'Search failed'
  } finally {
    loading.value = false
  }
}

const debouncedSearch = debounce(doSearch, 500)

watch(
  () => query.value,
  () => {
    debouncedSearch()
  },
)

const startManualPlacement = () => {
  window.dispatchEvent(
    new CustomEvent('start-waypoint-placement', {
      detail: {
        zoom: 16,
        scope: props.placementScope,
      },
    }),
  )
  emit('close')
  if (isSmallScreen.value) {
    emit('close-tour-modal-and-collapse-mobile-toolbar')
  }
}

const selectResult = (r: NominatimResult) => {
  const locationName = getLocationName(r)
  const formattedAddress = formatAddress(r)
  const waypointType = mapSearchResultToPOIType(r.type, r.category)

  window.dispatchEvent(
    new CustomEvent('start-waypoint-placement', {
      detail: {
        flyTo: { lat: r.lat, lon: r.lon, zoom: 16 },
        name: locationName,
        description: formattedAddress,
        type: waypointType,
        scope: props.placementScope,
      },
    }),
  )
  emit('close')
  if (isSmallScreen.value) {
    emit('close-tour-modal-and-collapse-mobile-toolbar')
  }
}

const handleOpen = () => {
  // Reset transient state each time modal opens
  query.value = ''
  results.value = []
  error.value = null
  hasSearched.value = false
}

watch(
  () => props.isOpen,
  (open) => {
    if (open) handleOpen()
  },
)
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :title="isPOIScope() ? 'Add POI' : 'Add Waypoint'"
    size="lg"
    :show-footer="false"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <!-- Mode buttons (stacked on mobile, inline desktop) -->
      <div
        class="flex"
        :class="[
          isSmallScreen ? 'flex-col gap-3' : 'flex-row gap-3 items-center justify-start flex-wrap',
        ]"
      >
        <button
          class="px-4 py-2 rounded-md bg-primary-600 text-white text-sm font-medium shadow hover:bg-primary-700 transition-colors hover:cursor-pointer"
          @click="startManualPlacement"
        >
          Place Manually
        </button>

        <div class="flex-1 flex flex-col gap-2 min-w-[220px]">
          <label class="text-xs font-medium text-gray-600">Search (OpenStreetMap)</label>
          <input
            v-model="query"
            type="text"
            class="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
            placeholder="Search for a place, address, POI..."
            autocomplete="off"
            :aria-label="
              isPOIScope() ? 'Search location to place POI' : 'Search location to place waypoint'
            "
            @focus="getInputHandlers().onFocus"
            @blur="getInputHandlers().onBlur"
          />
        </div>
      </div>

      <!-- Search status -->
      <div v-if="loading" class="text-sm text-gray-500 animate-pulse">Searching...</div>
      <div v-else-if="error" class="text-sm text-red-600">
        {{ error }}
      </div>
      <div
        v-else-if="!loading && hasSearched && results.length === 0"
        class="text-sm text-gray-500"
      >
        No results.
      </div>

      <!-- Results -->
      <ul v-if="results.length" class="divide-y divide-gray-100 rounded-md border border-gray-200">
        <li
          v-for="r in results"
          :key="`${r.lat}-${r.lon}-${r.displayName}`"
          class="p-3 hover:bg-primary-50 transition-colors cursor-pointer flex flex-col gap-0.5"
          @click="selectResult(r)"
        >
          <span class="text-sm font-medium text-gray-800 truncate">
            {{ r.displayName }}
          </span>
          <span class="text-[11px] uppercase tracking-wide text-gray-500">
            {{ r.category || r.type }}
          </span>
        </li>
      </ul>

      <p class="text-[11px] text-gray-400 leading-snug">
        After selecting a search result (or manual), a crosshair will appear. Use pan/zoom to refine
        placement, then confirm.
      </p>
    </div>
  </BaseModal>
</template>
